import { Home, LineChart, Plus, Scale, Sparkles, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router';
import { APP_NAME } from '../shared/brand';
import { browserTimeZone } from '../shared/dates';
import { useSettings, useUpdateProfile } from './api/hooks';
import { BrandMark } from './components/BrandMark';
import { CoachPanel } from './components/CoachPanel';
import { prefetchCatalogWhenIdle } from './lib/catalog';
import { useUI } from './lib/ui';
import { CoachPage } from './pages/CoachPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { ProgressPage } from './pages/ProgressPage';
import { TodayPage } from './pages/TodayPage';
import { YouPage } from './pages/YouPage';

const NAV = [
  { to: '/', label: 'Today', icon: Home },
  { to: '/progress', label: 'Progress', icon: LineChart },
  { to: '/coach', label: 'Coach', icon: Sparkles },
  { to: '/you', label: 'You', icon: UserRound },
] as const;

export function App() {
  const { openLog, openWeighIn } = useUI();
  const location = useLocation();
  const settings = useSettings();
  const updateProfile = useUpdateProfile();
  const [scrolled, setScrolled] = useState(false);
  const onCoach = location.pathname.startsWith('/coach');
  const serverTz = settings.data?.profile.timezone;

  useEffect(() => {
    document.title = APP_NAME;
    prefetchCatalogWhenIdle();
  }, []);
  // Keep the server's idea of "today" (insights, streaks, recaps) in the browser's time zone.
  useEffect(() => {
    const tz = browserTimeZone();
    if (serverTz && serverTz !== tz) updateProfile.mutate({ timezone: tz });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverTz]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Block body on purpose: modern browsers return a Promise from scrollTo, and an
  // effect must return nothing or a cleanup function.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // A fresh personal account goes through setup before anything else.
  if (settings.data && !settings.data.account.onboarded) return <OnboardingPage />;

  return (
    <div className={`shell ${onCoach ? '' : 'has-coach'}`}>
      <a href="#main" className="sr-only">
        Skip to content
      </a>
      <aside className="sidebar" aria-label="Main navigation">
        <NavLink to="/" className="brand">
          <BrandMark /> {APP_NAME}
        </NavLink>
        <nav className="sidebar__nav">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `navlink ${isActive ? 'is-active' : ''}`}>
              <Icon size={19} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__foot">
          <button className="btn btn--primary btn--block" onClick={() => openLog()}>
            <Plus size={18} /> Log food
          </button>
          <button className="btn btn--ghost btn--block" onClick={() => openWeighIn()}>
            <Scale size={17} /> Weigh in
          </button>
          <span className="tiny faint center">
            {!settings.data?.ai.enabled ? 'AI off' : settings.data.ai.provider === 'stub' ? 'AI demo mode (offline)' : `AI · ${settings.data.ai.provider}`}
            {settings.data?.account.mode === 'demo' ? ' · demo data' : ''}
          </span>
        </div>
      </aside>

      <header className={`topbar ${scrolled ? 'is-scrolled' : ''}`}>
        <NavLink to="/" className="brand">
          <BrandMark /> {APP_NAME}
        </NavLink>
        <span className="grow" />
        <button className="btn btn--ghost btn--sm" onClick={() => openWeighIn()}>
          <Scale size={16} /> Weigh in
        </button>
      </header>

      <main id="main" className="main">
        <Routes>
          <Route path="/" element={<TodayPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/coach" element={<CoachPage />} />
          <Route path="/you" element={<YouPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {!onCoach && (
        <aside className="coach-rail" aria-label="Coach">
          <CoachPanel />
        </aside>
      )}

      <nav className="tabbar" aria-label="Main">
        {NAV.slice(0, 2).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `tabbar__item ${isActive ? 'is-active' : ''}`}>
            <Icon size={22} /> {label}
          </NavLink>
        ))}
        <button className="tabbar__log" aria-label="Log food" onClick={() => openLog()}>
          <Plus size={26} />
        </button>
        {NAV.slice(2).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `tabbar__item ${isActive ? 'is-active' : ''}`}>
            <Icon size={22} /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
